import { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatApiError, downloadFile } from "@/lib/api";
import { ScoreTrendChart } from "@/components/ScoreTrendChart";
import { PartnerReport, PartnerTryouts, PartnerUploader } from "@/components/PartnerSections";
import SystemInsight, { TryoutInsight } from "@/components/SystemInsight";
import { RecognizeSection, PosttestSection } from "@/components/LearningSections";
import MotivasiQuote from "@/components/MotivasiQuote";
import { toast } from "sonner";
import { UserPlus, Users, Target, TrendingUp, Trash2, Compass, Hash, Download, GraduationCap, Star, Briefcase, Lightbulb, FileText } from "lucide-react";

export default function ParentDashboard() {
  const { user } = useAuth();
  const [children, setChildren] = useState([]);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState({});
  const [learning, setLearning] = useState({});
  const [monthly, setMonthly] = useState({});

  useEffect(() => {
    children.forEach((c) => {
      const hasData = c.partner_report || (c.partner_tryouts || []).length > 0;
      if (hasData && insights[c.user.id] === undefined) {
        api.get(`/parent/child-insight/${c.user.id}`).then((r) => setInsights((p) => ({ ...p, [c.user.id]: r.data.insight }))).catch(() => {});
      }
      if (learning[c.user.id] === undefined) {
        Promise.all([
          api.get(`/learning/recognize?child_id=${c.user.id}`).then((r) => r.data).catch(() => null),
          api.get(`/learning/posttest?child_id=${c.user.id}`).then((r) => r.data).catch(() => null),
        ]).then(([rec, pos]) => setLearning((p) => ({ ...p, [c.user.id]: { recognize: rec, posttest: pos } })));
      }
      if (monthly[c.user.id] === undefined) {
        api.get(`/report/monthly/view?child_id=${c.user.id}`).then((r) => setMonthly((p) => ({ ...p, [c.user.id]: r.data }))).catch(() => {});
      }
    });
  }, [children]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = () => api.get("/parent/children").then((r) => setChildren(r.data.children)).catch(() => {});
  useEffect(() => { load(); }, []);

  const link = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/parent/link", { child_email: email });
      toast.success("Akun anak berhasil ditautkan");
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const linkByCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/parent/link-code", { code });
      toast.success("Akun anak berhasil ditautkan");
      setCode("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadChild = async (id, name) => {
    try { await downloadFile(`/report/pdf/child/${id}`, `Rapor-${name}.pdf`); toast.success("Rapor PDF diunduh"); }
    catch { toast.error("Unduh gagal"); }
  };

  const downloadMonthly = async (id, name) => {
    try { await downloadFile(`/report/monthly/pdf?child_id=${id}`, `Laporan-Bulanan-${name}.pdf`); toast.success("Laporan Bulanan diunduh"); }
    catch (e) { toast.error(e.response?.data?.detail || "Unduh gagal"); }
  };

  const unlink = async (id) => {
    await api.delete(`/parent/link/${id}`);
    toast.success("Tautan dihapus");
    load();
  };

  return (
    <AppShell>
      <div className="mb-8">
        <p className="text-[#5C5F5C]">Dashboard Orang Tua</p>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-[#1A1C1A]">{user?.name}</h1>
        <p className="text-[#5C5F5C] mt-2 max-w-xl">Tautkan akun anak untuk memantau posisi, hasil asesmen, dan langkah perjalanan mereka.</p>
      </div>

      <form onSubmit={link} className="clay-card p-6 mb-4 flex flex-wrap items-end gap-3" data-testid="link-child-form">
        <label className="flex-1 min-w-[240px]">
          <span className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Tautkan via email akun anak (siswa)</span>
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="child-email-input"
            placeholder="email anak yang sudah terdaftar" className="w-full px-4 py-3 rounded-lg border border-[#E5E0D8] focus:border-[#E25E3E] focus:ring-2 focus:ring-[#E25E3E]/20 outline-none" />
        </label>
        <button type="submit" disabled={loading} data-testid="link-child-btn"
          className="px-6 py-3 rounded-full bg-[#E25E3E] text-white font-medium hover:bg-[#C94F31] transition-colors disabled:opacity-60 flex items-center gap-2">
          <UserPlus className="h-4 w-4" /> {loading ? "Menautkan…" : "Tautkan"}
        </button>
      </form>

      <form onSubmit={linkByCode} className="clay-card p-6 mb-8 flex flex-wrap items-end gap-3 bg-[#F4B942]/15 border-[#F4B942]/40" data-testid="link-code-form">
        <label className="flex-1 min-w-[240px]">
          <span className="block text-sm font-medium text-[#1A1C1A] mb-1.5">Atau tautkan via kode undangan anak</span>
          <input required value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="child-code-input" maxLength={6}
            placeholder="6 karakter (mis. A1B2C3)" className="w-full px-4 py-3 rounded-lg border border-[#E5E0D8] focus:border-[#E25E3E] focus:ring-2 focus:ring-[#E25E3E]/20 outline-none tracking-widest font-semibold uppercase" />
        </label>
        <button type="submit" disabled={loading} data-testid="link-code-btn"
          className="px-6 py-3 rounded-full bg-[#1A1C1A] text-white font-medium hover:opacity-90 transition-opacity disabled:opacity-60 flex items-center gap-2">
          <Hash className="h-4 w-4" /> {loading ? "Menautkan…" : "Tautkan dgn Kode"}
        </button>
      </form>

      <div className="flex items-center gap-2 mb-4">
        <Users className="h-5 w-5 text-[#4A6B53]" />
        <h2 className="font-heading font-black text-2xl text-[#1A1C1A]">Anak Tertaut ({children.length})</h2>
      </div>

      {children.length === 0 ? (
        <div className="clay-card p-10 text-center text-[#5C5F5C]">
          <Users className="h-12 w-12 mx-auto mb-3 text-[#E5E0D8]" />
          Belum ada anak yang ditautkan. Masukkan email akun siswa anak Anda di atas.
        </div>
      ) : (
        <div className={children.length === 1 ? "grid grid-cols-1 gap-6" : "grid md:grid-cols-2 gap-6"}>
          {children.map((c) => {
            const top = c.assessment?.scores?.tipe_dominan || [];
            const rec = c.assessment?.scores?.rekomendasi?.[0];
            return (
              <div key={c.user.id} className="clay-card p-6" data-testid={`child-card-${c.user.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading font-bold text-xl text-[#1A1C1A]">{c.user.name}</h3>
                    <p className="text-sm text-[#5C5F5C]">{c.user.email} · Kelas {c.user.kelas || "-"}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => downloadChild(c.user.id, c.user.name)} data-testid={`child-pdf-${c.user.id}`} className="p-2 rounded-full text-[#5C5F5C] hover:bg-[#4A6B53]/10 hover:text-[#4A6B53] transition-colors" title="Unduh Rapor PDF">
                      <Download className="h-4 w-4" />
                    </button>
                    <button onClick={() => unlink(c.user.id)} data-testid={`unlink-${c.user.id}`} className="p-2 rounded-full text-[#5C5F5C] hover:bg-[#D9381E]/10 hover:text-[#D9381E] transition-colors" title="Hapus tautan">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {(() => {
                  const m = monthly[c.user.id];
                  return (
                    <div className="mb-4 rounded-xl bg-[#E25E3E]/8 border border-[#E25E3E]/25 p-4" data-testid={`laporan-bulanan-${c.user.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-[#E25E3E]" /><p className="text-sm font-bold text-[#E25E3E]">Laporan Bulanan</p></div>
                        {m?.available && (
                          <button onClick={() => downloadMonthly(c.user.id, c.user.name)} data-testid={`monthly-download-${c.user.id}`}
                            className="px-3 py-1.5 rounded-full bg-[#E25E3E] text-white text-xs font-medium inline-flex items-center gap-1.5 hover:bg-[#C94F31] transition-colors">
                            <Download className="h-3.5 w-3.5" /> Unduh PDF
                          </button>
                        )}
                      </div>
                      {m?.available ? (
                        <div className="text-sm text-[#1A1C1A] space-y-1.5">
                          {(m.period_label || m.kelas) && (
                            <p className="text-xs text-[#5C5F5C]">{m.period_label ? `Periode: ${m.period_label}` : ""}{m.kelas ? `${m.period_label ? " · " : ""}Kelas ${m.kelas}` : ""}</p>
                          )}
                          {m.analisa && <p className="leading-relaxed whitespace-pre-line">{m.analisa}</p>}
                          {(m.avg != null || m.attendance) && (
                            <div className="flex flex-wrap gap-2 pt-1">
                              {m.avg != null && <span className="text-xs px-2 py-1 rounded-full bg-white border border-[#E5E0D8]">Rata-rata postes: <b>{m.avg}%</b>{m.category ? ` · ${m.category}` : ""}</span>}
                              {m.attendance && <span className="text-xs px-2 py-1 rounded-full bg-white border border-[#E5E0D8]">Hadir {m.attendance.hadir || 0}/{m.attendance.total || 0}</span>}
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-[#5C5F5C]">Laporan bulanan belum dipublikasikan admin. Akan muncul di sini (bisa dibaca & diunduh) setelah admin menerbitkannya.</p>
                      )}
                    </div>
                  );
                })()}

                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[#5C5F5C]">Progres Perjalanan</span>
                  <span className="font-heading font-black text-lg text-[#E25E3E]">{c.progress.persen}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-[#F0ECE4] overflow-hidden mb-4">
                  <div className="h-full bg-[#E25E3E] rounded-full" style={{ width: `${c.progress.persen}%` }} />
                </div>

                {c.assessment ? (
                  <div className="rounded-xl bg-[#FAF8F5] p-4 mb-3">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#5C5F5C] mb-1"><Target className="h-3.5 w-3.5" /> Posisi Anak</div>
                    <p className="text-sm text-[#1A1C1A] leading-relaxed">{c.assessment.narrative?.ringkasan}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {top.map((t) => <span key={t.kode} className="text-xs px-2 py-1 rounded-full bg-[#4A6B53]/10 text-[#4A6B53]">{t.label}</span>)}
                    </div>
                  </div>
                ) : (c.partner_report ? null : (
                  <p className="text-sm text-[#5C5F5C] mb-3">Anak belum mengikuti Asesmen Potensi Diri.</p>
                ))}

                {c.career_share && (
                  <div className="mb-3 rounded-xl border-2 border-[#F4B942]/50 bg-[#F4B942]/10 p-4" data-testid={`career-share-${c.user.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#8a6d16]"><Star className="h-3.5 w-3.5 fill-[#F4B942]" /> Rapor Karier Dibagikan</div>
                      <span className="text-[10px] text-[#5C5F5C]">{c.career_share.shared_at ? new Date(c.career_share.shared_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" }) : ""}</span>
                    </div>
                    {c.career_share.cita_cita && (
                      <p className="text-sm text-[#1A1C1A] mb-2"><span className="font-semibold">Cita-cita:</span> {c.career_share.cita_cita}</p>
                    )}
                    {c.career_share.profesi?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-xs text-[#5C5F5C] mb-1 flex items-center gap-1"><Briefcase className="h-3 w-3" /> Profesi paling sesuai</p>
                        <div className="flex flex-wrap gap-1.5">
                          {c.career_share.profesi.map((p, i) => <span key={i} className="text-xs px-2 py-1 rounded-full bg-white text-[#1A1C1A] border border-[#E5E0D8]">{p.nama}</span>)}
                        </div>
                      </div>
                    )}
                    {c.career_share.mapel_prioritas?.length > 0 && (
                      <p className="text-xs text-[#5C5F5C]"><span className="font-semibold text-[#1A1C1A]">Mapel prioritas:</span> {c.career_share.mapel_prioritas.map((m) => m.mapel).join(", ")}</p>
                    )}
                    {c.career_share.jalur_rekomendasi?.nama && (
                      <p className="text-xs text-[#5C5F5C] mt-1"><span className="font-semibold text-[#1A1C1A]">Jalur disarankan:</span> {c.career_share.jalur_rekomendasi.nama}</p>
                    )}
                  </div>
                )}

                {c.cita_alternatif?.result?.alternatif?.length > 0 && (
                  <div className="mb-3 rounded-xl border border-[#4A6B53]/30 bg-[#4A6B53]/5 p-4" data-testid={`cita-alternatif-${c.user.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#4A6B53]"><Compass className="h-3.5 w-3.5" /> Eksplorasi Cita-cita Anak</div>
                      <span className="text-[10px] text-[#5C5F5C]">{c.cita_alternatif.updated_at ? new Date(c.cita_alternatif.updated_at).toLocaleDateString("id-ID", { day: "numeric", month: "short" }) : ""}</span>
                    </div>
                    <p className="text-xs text-[#5C5F5C] mb-2 leading-relaxed">{c.user.name.split(" ")[0]} merasa anjuran awal belum pas & mencari alternatif yang tetap selaras potensinya. Bahan bagus untuk diskusi bareng:</p>
                    <div className="flex flex-wrap gap-1.5 mb-2">
                      {c.cita_alternatif.result.alternatif.map((a, i) => (
                        <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-white text-[#1A1C1A] border border-[#E5E0D8]">{a.bidang}</span>
                      ))}
                    </div>
                    {c.cita_alternatif.result.catatan_penyeimbang && (
                      <p className="text-[11px] text-[#5C5F5C] leading-relaxed"><span className="font-semibold text-[#1A1C1A]">Catatan:</span> {c.cita_alternatif.result.catatan_penyeimbang}</p>
                    )}
                  </div>
                )}

                <div className="mb-3" data-testid={`partner-block-${c.user.id}`}>
                  <div className="text-xs font-bold uppercase tracking-wide text-[#9C9A94] mb-2">Bagian 1 · Profil Diri (Asesmen)</div>
                  <PartnerReport report={c.partner_report} forParent />
                  {(c.assessment || c.partner_report)
                    ? <SystemInsight insight={insights[c.user.id]} forParent />
                    : <p className="text-sm text-[#9C9A94] mb-3">Belum ada data asesmen. Ikuti asesmen untuk melihat profil lengkap.</p>}
                  <PartnerUploader childId={c.user.id} onDone={load} className="mb-3" />

                  <div className="text-xs font-bold uppercase tracking-wide text-[#9C9A94] mb-2 mt-5">Bagian 2 · Recognize (Diagnostik Awal)</div>
                  <RecognizeSection report={learning[c.user.id]?.recognize?.report} analisa={learning[c.user.id]?.recognize?.analisa} forParent />

                  <div className="text-xs font-bold uppercase tracking-wide text-[#9C9A94] mb-2 mt-5">Bagian 3 · Postes Harian</div>
                  <PosttestSection items={learning[c.user.id]?.posttest?.items} analisa={learning[c.user.id]?.posttest?.analisa} forParent />

                  <div className="text-xs font-bold uppercase tracking-wide text-[#9C9A94] mb-2 mt-5">Bagian 4 · TryOut</div>
                  {(() => {
                    const realTryouts = (c.partner_tryouts || []).filter((t) => !/postes|recognize|baseline|pretest|postest|diagnos/i.test(t.batch_name || ""));
                    return realTryouts.length ? (
                      <>
                        <TryoutInsight insight={insights[c.user.id]} forParent />
                        <PartnerTryouts items={realTryouts} />
                      </>
                    ) : <p className="text-sm text-[#9C9A94]">Belum ada data TryOut. Ikuti TryOut untuk melihat perkembangan nilai.</p>;
                  })()}
                  {(() => {
                    const trendScores = (c.tryout_trend || []).map((t) => t.skor).filter((n) => typeof n === "number");
                    const partnerScores = (c.partner_tryouts || []).map((t) => t?.hasil?.persen_benar).filter((n) => typeof n === "number");
                    const all = [...trendScores, ...partnerScores];
                    if (!all.length) return null;
                    const avg = Math.round(all.reduce((a, b) => a + b, 0) / all.length);
                    return <MotivasiQuote skor={avg} className="mt-3" />;
                  })()}
                </div>


                {(c.journey?.target_pendidikan?.prodi || c.journey?.target_pendidikan?.ptn || c.favorite_profesi?.nama) && (
                  <div className="mb-3 rounded-xl border border-[#4A6B53]/30 bg-[#4A6B53]/5 p-4" data-testid={`child-target-${c.user.id}`}>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#4A6B53] mb-2"><GraduationCap className="h-3.5 w-3.5" /> Target & Impian Anak</div>
                    {(c.journey?.target_pendidikan?.prodi || c.journey?.target_pendidikan?.ptn) && (
                      <p className="text-sm text-[#1A1C1A] mb-1"><span className="font-semibold">Target pendidikan:</span> {[c.journey.target_pendidikan.prodi, c.journey.target_pendidikan.ptn].filter(Boolean).join(" · ")}</p>
                    )}
                    {c.favorite_profesi?.nama && (
                      <p className="text-sm text-[#1A1C1A] flex items-center gap-1.5"><Star className="h-3.5 w-3.5 fill-[#F4B942] text-[#F4B942]" /> <span className="font-semibold">Profesi impian:</span> {c.favorite_profesi.nama}</p>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-[#E5E0D8] p-3">
                    <div className="flex items-center gap-1.5 text-xs text-[#5C5F5C] mb-1"><Compass className="h-3.5 w-3.5" /> Bidang Sesuai</div>
                    <p className="text-sm font-semibold text-[#1A1C1A]">{rec?.nama || "-"}</p>
                  </div>
                  <div className="rounded-xl border border-[#E5E0D8] p-3">
                    <div className="flex items-center gap-1.5 text-xs text-[#5C5F5C] mb-1"><TrendingUp className="h-3.5 w-3.5" /> TryOut Terbaik</div>
                    <p className="text-sm font-semibold text-[#1A1C1A]">{c.tryout_terbaik != null ? `${c.tryout_terbaik} (${c.tryout_count}x)` : "Belum ada"}</p>
                  </div>
                </div>

                {c.tryout_trend?.length > 1 && (
                  <div className="mt-4 pt-4 border-t border-[#E5E0D8]" data-testid={`child-trend-${c.user.id}`}>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#5C5F5C] mb-2"><TrendingUp className="h-3.5 w-3.5" /> Tren Skor TryOut</div>
                    <ScoreTrendChart data={c.tryout_trend} height={170} />
                  </div>
                )}

                {c.prodi_targets?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-[#E5E0D8]" data-testid={`child-target-${c.user.id}`}>
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-[#5C5F5C] mb-2"><GraduationCap className="h-3.5 w-3.5" /> Target Prodi (vs skor UTBK terbaik: {c.utbk_best ?? "—"})</div>
                    <div className="space-y-1.5">
                      {c.prodi_targets.map((p, idx) => { const ok = c.utbk_best != null && c.utbk_best >= p.target; return (
                        <div key={idx} className="flex items-center justify-between text-xs gap-2">
                          <span className="text-[#1A1C1A] truncate">{p.prodi} · {p.ptn}</span>
                          <span className={`px-2 py-0.5 rounded-full font-medium shrink-0 ${c.utbk_best == null ? "bg-[#F0ECE4] text-[#5C5F5C]" : ok ? "bg-[#4A6B53]/15 text-[#4A6B53]" : "bg-[#D9381E]/10 text-[#D9381E]"}`}>{c.utbk_best == null ? "Belum ada TO" : ok ? `Tercapai (≥${p.target})` : `Belum (${p.target})`}</span>
                        </div> ); })}
                    </div>
                    <p className="text-[10px] text-[#5C5F5C] mt-1.5">*Estimasi keketatan, bukan kelulusan resmi.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </AppShell>
  );
}
